import { useState } from "react";
import { Link } from "react-router-dom";
import { SectionShell } from "@/pages/SectionShell";
import { EmptyState } from "@/components/EmptyState";
import { useI18n, useT } from "@/contexts/I18nContext";
import { useGroups } from "@/lib/hooks/usePeople";
import type { Group } from "@/types";
import { collator as collatorFor } from "@/i18n";

/**
 * Every group in the institution.
 *
 * Archived ones are hidden rather than removed: a studio that runs the same
 * course every autumn wants last year's out of the way and back next August.
 */
export function GroupsPage() {
  const t = useT();
  const { locale } = useI18n();
  const { groups, loading } = useGroups();
  const [showArchived, setShowArchived] = useState(false);

  const shown = groups
    .filter((group) => showArchived || !group.archivedAt)
    .sort((a, b) => collatorFor(locale).compare(a.name, b.name));

  return (
    <SectionShell
      titleKey="groups.title"
      actions={
        <Link
          to="/groups/new"
          className="inline-flex items-center min-h-10 rounded-control bg-brand px-4 py-2
                     text-[14px] font-medium leading-5 text-white hover:bg-brand-hover"
        >
          {t("groups.new")}
        </Link>
      }
    >
      <label className="flex items-center gap-2 text-sm text-ink-soft">
        <input
          type="checkbox"
          checked={showArchived}
          onChange={(event) => setShowArchived(event.target.checked)}
        />
        {t("groups.showArchived")}
      </label>

      <div className="mt-6">
        {loading ? (
          <p className="py-10 text-center text-sm text-ink-muted">{t("common.loading")}</p>
        ) : shown.length === 0 ? (
          <EmptyState titleKey="groups.empty" bodyKey="groups.emptyBody" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[14px]">
              <caption className="sr-only">{t("groups.title")}</caption>
              <thead>
                <tr className="border-b border-line bg-surface-muted text-left text-ink-muted">
                  <th scope="col" className="h-9 px-3 text-[13px] font-medium">{t("groups.column.name")}</th>
                  <th scope="col" className="h-9 px-3 text-[13px] font-medium">{t("groups.column.ageBand")}</th>
                  <th scope="col" className="h-9 px-3 text-[13px] font-medium">{t("groups.column.teachers")}</th>
                  <th scope="col" className="h-9 px-3 text-[13px] font-medium">{t("groups.column.students")}</th>
                  <th scope="col" className="h-9 px-3 text-[13px] font-medium">{t("groups.column.mode")}</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((group) => (
                  <GroupRow key={group.id} group={group} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </SectionShell>
  );
}

function GroupRow({ group }: { group: Group }) {
  const t = useT();
  return (
    <tr className="border-b border-line last:border-0">
      <td className="h-10 px-3 py-2.5">
        <Link
          to={`/groups/${group.id}`}
          className="font-medium text-ink underline-offset-2 hover:underline"
        >
          {group.name}
        </Link>
        {group.archivedAt ? (
          <span className="ml-2 text-xs text-ink-faint">{t("groups.archived")}</span>
        ) : null}
      </td>
      <td className="h-10 px-3 py-2.5 text-ink-muted">{t(`import.ageBand.${group.ageBand}`)}</td>
      <td className="h-10 px-3 py-2.5 text-ink-muted">{group.teacherUids.length}</td>
      <td className="h-10 px-3 py-2.5 text-ink-muted">{group.roster.length}</td>
      {/* "Connects: Online" said nothing a teacher could act on. */}
      <td className="h-10 px-3 py-2.5 text-ink-muted">{t(`group.mode.${group.defaultMode}`)}</td>
    </tr>
  );
}
